import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Search, Upload, AlertCircle, Loader2, TrendingUp, Info } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CsvUploadDialog } from '@/components/CsvUploadDialog';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useSaveEvaluation } from '@/hooks/useEvaluations';
import { useAddToWatchlist } from '@/hooks/useWatchlist';
import { fetchFinancialData } from '@/lib/financialDataService';
import { buildEvaluationReport } from '@/lib/analysisEngine';
import type { CsvImportData } from '@/types/stock';

export default function AnalyzePage() {
  useSeoMeta({ title: 'Analyze Stock — Stock Fundamentals', description: 'Run a fundamental analysis on any stock ticker.' });

  const navigate = useNavigate();
  const { data: settings } = useAppSettings();
  const { mutateAsync: saveEvaluation } = useSaveEvaluation();
  const { mutateAsync: addToWatchlist } = useAddToWatchlist();

  const [ticker, setTicker] = useState('');
  const [startYear, setStartYear] = useState(new Date().getFullYear() - 9);
  const [endYear] = useState(new Date().getFullYear() - 1);
  const [profitMarginTarget, setProfitMarginTarget] = useState(
    settings?.defaultProfitMarginTarget ?? 15,
  );
  const [currentRatioTarget, setCurrentRatioTarget] = useState(
    settings?.defaultCurrentRatioTarget ?? 1.0,
  );

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvData, setCsvData] = useState<CsvImportData | null>(null);

  const yearRange = endYear - startYear + 1;

  const handleAnalyze = useCallback(async () => {
    if (!ticker.trim()) {
      setError('Please enter a ticker symbol.');
      return;
    }
    setError(null);
    setLoading(true);
    setLoadingStep('');

    try {
      const t = ticker.toUpperCase().trim();
      const provider = settings?.apiSettings.provider ?? 'none';
      const avKey = settings?.apiSettings.alphaVantageKey ?? '';
      const fmpKey = settings?.apiSettings.fmpKey ?? '';

      let financialData;
      if (csvData) {
        // Build financial data from CSV
        financialData = {
          income: csvData.incomeStatements,
          balance: csvData.balanceSheets,
          cashFlow: csvData.cashFlows,
          overview: {
            ticker: t,
            name: t,
            exchange: 'Unknown',
            sector: 'Unknown' as const,
            industry: 'Unknown',
            marketCap: 0,
            peRatio: null,
            pbRatio: null,
            dividendYield: null,
            beta: null,
            description: '',
            country: '',
            currency: 'USD',
          },
        };
      } else {
        setLoadingStep('Fetching income statements…');
        financialData = await fetchFinancialData(t, provider, avKey, fmpKey, setLoadingStep);
      }

      const report = buildEvaluationReport(
        uuidv4(),
        t,
        financialData,
        startYear,
        endYear,
        { profitMarginTarget, currentRatioTarget },
        csvData ? 'csv' : provider,
      );

      await saveEvaluation(report);

      // Auto-add to watchlist
      await addToWatchlist({
        ticker: t,
        companyName: report.companyName,
        exchange: report.exchange,
        addedAt: Math.floor(Date.now() / 1000),
        lastEvaluationId: report.id,
        lastEvaluationDate: report.evaluationDate,
        lastEvaluationStatus: report.overallStatus,
      });

      navigate(`/evaluation/${report.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  }, [ticker, startYear, endYear, profitMarginTarget, currentRatioTarget, settings, csvData, saveEvaluation, addToWatchlist, navigate]);

  const hasApiKey = settings?.apiSettings.provider !== 'none' &&
    ((settings?.apiSettings.provider === 'alpha_vantage' && settings?.apiSettings.alphaVantageKey) ||
     (settings?.apiSettings.provider === 'fmp' && settings?.apiSettings.fmpKey));

  return (
    <AppLayout>
      <div className="max-w-screen-lg mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Analyze a Stock</h1>
          <p className="text-muted-foreground mt-1">
            Enter a ticker symbol to run a comprehensive fundamental analysis.
          </p>
        </div>

        {!hasApiKey && !csvData && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              No API key configured. You can{' '}
              <a className="underline cursor-pointer" onClick={() => navigate('/settings')}>add an API key in Settings</a>{' '}
              or upload CSV files manually.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Input */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Ticker Symbol
                </CardTitle>
                <CardDescription>
                  Enter the stock ticker symbol (e.g., AAPL, MSFT, GOOGL)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="e.g. AAPL"
                      value={ticker}
                      onChange={(e) => setTicker(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                      className="text-lg font-mono font-bold uppercase"
                    />
                  </div>
                  <Button
                    onClick={handleAnalyze}
                    disabled={loading || (!hasApiKey && !csvData)}
                    size="default"
                    className="gap-2"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {loadingStep || 'Analyzing…'}</>
                    ) : (
                      <><Search className="w-4 h-4" /> Analyze</>
                    )}
                  </Button>
                </div>

                {csvData && (
                  <div className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 rounded-md text-sm text-emerald-700 dark:text-emerald-300">
                    <span>✓ CSV data loaded ({csvData.incomeStatements.length} years)</span>
                    <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={() => setCsvData(null)}>
                      Clear
                    </Button>
                  </div>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Analysis Period */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Analysis Period</CardTitle>
                <CardDescription>
                  Select the fiscal year range for analysis (up to 10 years)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Start Year</Label>
                    <span className="text-sm font-mono font-bold text-primary">
                      {startYear} – {endYear}
                    </span>
                  </div>
                  <Slider
                    min={new Date().getFullYear() - 15}
                    max={endYear - 1}
                    step={1}
                    value={[startYear]}
                    onValueChange={([v]) => setStartYear(v)}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {yearRange}-year lookback ({startYear} to {endYear})
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Criteria Targets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evaluation Targets</CardTitle>
                <CardDescription>
                  Customize pass/fail thresholds for the analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1.5">
                      Net Profit Margin Target
                      <Tooltip>
                        <TooltipTrigger asChild><button type="button"><Info className="w-3.5 h-3.5 text-muted-foreground" /></button></TooltipTrigger>
                        <TooltipContent>Minimum average net income / revenue ratio to pass</TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-sm font-mono font-bold text-primary">
                      {profitMarginTarget}%
                    </span>
                  </div>
                  <Slider
                    min={1} max={40} step={1}
                    value={[profitMarginTarget]}
                    onValueChange={([v]) => setProfitMarginTarget(v)}
                  />
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1.5">
                      Current Ratio Target
                      <Tooltip>
                        <TooltipTrigger asChild><button type="button"><Info className="w-3.5 h-3.5 text-muted-foreground" /></button></TooltipTrigger>
                        <TooltipContent>Minimum current assets / current liabilities ratio to pass</TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-sm font-mono font-bold text-primary">
                      {currentRatioTarget.toFixed(1)}x
                    </span>
                  </div>
                  <Slider
                    min={0.5} max={3.0} step={0.1}
                    value={[currentRatioTarget]}
                    onValueChange={([v]) => setCurrentRatioTarget(v)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">Manual CSV Upload</CardTitle>
                <CardDescription className="text-xs">
                  No API key? Upload your own financial data from CSV files.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCsvOpen(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV Files
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">What We Analyze</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>Profit margins (net, gross, operating)</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>Revenue & EPS growth consistency</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>Balance sheet health (current ratio)</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>Free cash flow trends</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>GICS sector classification & moat indicators</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-primary mt-0.5">▸</span>
                    <span>PASS / FAIL / WATCH scoring</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Data Sources</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-1.5">
                <p>
                  <strong className="text-foreground">Alpha Vantage</strong> — Free: 25 calls/day. No credit card needed. ✅ Recommended.
                </p>
                <p>
                  <strong className="text-foreground">Financial Modeling Prep</strong> — Financial statements require a paid plan. Free tier only covers quotes.
                </p>
                <p>
                  All data is cached for 24h to conserve API quota.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <CsvUploadDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImport={(data) => {
          setCsvData(data);
          setCsvOpen(false);
        }}
      />
    </AppLayout>
  );
}
